/**
 * hollowlog-want-list.js
 *
 * Standalone "want list checker" page: a shopper pastes or uploads a
 * list of cards they're after (name / number / set, any combination,
 * since real want lists are rarely complete or perfectly spelled) and
 * gets back which ones are actually in stock right now. Talks to
 * POST /api/bulk-match.
 *
 * Confidence-tiered results (2026-09-21 redesign): the backend now
 * classifies every candidate as exact/likely/similar/no_match instead
 * of a flat list of "matches" - this file renders that as a review
 * queue, not a search-results page. A same-name card from the wrong
 * set/number is shown as "no exact match" with the wrong card visually
 * subdued, never as if it were a confirmed hit. Only "exact" rows start
 * pre-selected; "likely" needs one tap to confirm ("Use this match");
 * "similar"/"no_match" need the shopper to either pick from an
 * expanded candidate grid or edit the entry.
 *
 * Self-contained by design, same integration pattern as
 * hollowlog-search.js: reads the API base from a
 * data-hollowlog-search-api marker element, does nothing if that's
 * missing or ENABLE_CUSTOM_SEARCH is off - no broken page either way.
 *
 * Adding to cart (2026-09-21): the point of this page is buying the
 * cards a shopper is after, so every hero card gets its own one-click
 * "Add to cart" button (same /cart/add.js AJAX pattern as
 * hollowlog-search.js's product cards), and the sticky bar bulk-adds
 * every confirmed row's chosen card to the real Shopify cart in one
 * request - not to a separate Wantlist. This intentionally doesn't
 * touch window.HollowlogWantlist at all.
 */
(function () {
  "use strict";

  const MAX_ROWS = 150;
  const PAGE_MARKER = "[data-hollowlog-want-list]";
  // Dawn's own stock section IDs for its cart drawer + header cart
  // icon bubble - requesting these via Shopify's Section Rendering API
  // in the same /cart/add.js call is how Dawn-based themes normally
  // refresh their cart UI after an AJAX add.
  const CART_SECTIONS_TO_REFRESH = "cart-drawer,cart-icon-bubble";

  function escapeHtml(str) {
    if (str == null) return "";
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function getApiBase() {
    const marker = document.querySelector("[data-hollowlog-search-api]");
    return marker ? marker.getAttribute("data-hollowlog-search-api") : null;
  }

  // /cart/add.js genuinely adds the item to the real Shopify cart
  // regardless of theme (a page reload would show it) - but nothing
  // tells the theme's own cart drawer/header bubble to refresh, so
  // they'd otherwise sit stale until the shopper navigates. Requesting
  // `sections` in the same add request returns freshly rendered HTML
  // for those sections (Shopify's Section Rendering API), which this
  // swaps in and opens the drawer if one exists on the page. Every
  // step is defensive/best-effort: if this specific theme customized
  // away from Dawn's stock IDs, this simply no-ops rather than
  // breaking the add-to-cart flow itself.
  function refreshCartUi(sections) {
    try {
      if (sections && sections["cart-icon-bubble"]) {
        const bubble = document.getElementById("cart-icon-bubble");
        if (bubble) {
          const parsed = new DOMParser().parseFromString(sections["cart-icon-bubble"], "text/html");
          const fresh = parsed.getElementById("cart-icon-bubble");
          bubble.innerHTML = fresh ? fresh.innerHTML : sections["cart-icon-bubble"];
        }
      }

      if (sections && sections["cart-drawer"]) {
        const drawer = document.querySelector("cart-drawer");
        if (drawer) {
          const parsed = new DOMParser().parseFromString(sections["cart-drawer"], "text/html");
          const fresh = parsed.querySelector("cart-drawer");
          if (fresh) drawer.innerHTML = fresh.innerHTML;
          if (typeof drawer.open === "function") drawer.open();
        }
      }
    } catch (error) {
      // Best-effort UI refresh only - the cart itself is already
      // correct server-side even if this part fails.
    }

    // Fire regardless of whether sections came back - other listeners
    // (this store's own or a theme customization) may care about "the
    // cart changed" without needing rendered section HTML.
    document.dispatchEvent(new CustomEvent("cart:refresh"));
    document.dispatchEvent(new CustomEvent("cart:updated"));
  }

  // --- Input parsing (unchanged from the previous version) --------------

  function splitCsvLine(line) {
    const cells = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (inQuotes) {
        if (char === '"' && line[i + 1] === '"') {
          current += '"';
          i++;
        } else if (char === '"') {
          inQuotes = false;
        } else {
          current += char;
        }
      } else if (char === '"') {
        inQuotes = true;
      } else if (char === ",") {
        cells.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    cells.push(current.trim());
    return cells;
  }

  const HEADER_KEYWORDS = {
    name: ["name", "card", "card name", "title", "pokemon", "pokémon"],
    number: ["number", "card number", "card no", "no", "no.", "#"],
    set: ["set", "set name", "expansion"],
  };

  function detectColumnMap(headerCells) {
    const lower = headerCells.map((c) => c.toLowerCase());
    const map = {};

    Object.keys(HEADER_KEYWORDS).forEach((field) => {
      const idx = lower.findIndex((cell) => HEADER_KEYWORDS[field].includes(cell));
      if (idx !== -1) map[field] = idx;
    });

    return map;
  }

  function parseCsv(text) {
    const lines = text
      .split(/\r\n|\r|\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    if (!lines.length) return { rows: [], error: "The file appears to be empty." };

    const firstCells = splitCsvLine(lines[0]);
    const columnMap = detectColumnMap(firstCells);
    const hasHeader = Object.keys(columnMap).length > 0;

    if (!hasHeader && firstCells.length > 1) {
      return {
        rows: [],
        error:
          "Couldn't tell which column is which. Add a header row with " +
          "any of: name, number, set (any order) - or use a single " +
          "column of just card names.",
      };
    }

    const dataLines = hasHeader ? lines.slice(1) : lines;
    if (!dataLines.length) {
      return { rows: [], error: "No card rows found below the header." };
    }
    if (dataLines.length > MAX_ROWS) {
      return { rows: [], error: `Please limit uploads to ${MAX_ROWS} cards at a time.` };
    }

    const rows = dataLines.map((line) => {
      const cells = splitCsvLine(line);
      if (!hasHeader) return { name: cells[0] || "" };

      return {
        name: columnMap.name != null ? cells[columnMap.name] || "" : "",
        number: columnMap.number != null ? cells[columnMap.number] || "" : "",
        set: columnMap.set != null ? cells[columnMap.set] || "" : "",
      };
    });

    return { rows, error: null };
  }

  // Free-text alternative to the CSV upload: one card per line, in any
  // mixed format ("Bulbasaur expedition 95", "Ludicolo sandstorm 7",
  // just a bare name, ...). The backend does its own name/set/number
  // split for a bare `name` field (see _parse_free_text in
  // routes_bulk_match.py) - this just splits into lines.
  function parsePastedLines(text) {
    const lines = text
      .split(/\r\n|\r|\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    if (!lines.length) return { rows: [], error: null };
    if (lines.length > MAX_ROWS) {
      return { rows: [], error: `Please limit to ${MAX_ROWS} cards at a time.` };
    }

    return { rows: lines.map((line) => ({ name: line })), error: null };
  }

  // --- Result card rendering ---------------------------------------------

  function formatPrice(price) {
    if (price == null || price === "") return "";
    const number = Number(price);
    return Number.isNaN(number) ? "" : "$" + number.toFixed(2);
  }

  function cardFinishLabel(doc) {
    if (doc.is_holo) return "Holo";
    if (doc.is_reverse_holo) return "Reverse Holo";
    return "";
  }

  // "Unlimited" is the unremarkable default for almost every card in
  // the catalog - only worth a label when it's actually a 1st
  // Edition/Shadowless print, the same way plain non-holo gets no
  // label above but Holo/Reverse Holo do.
  function cardEditionLabel(doc) {
    return doc.edition === "1st Edition" || doc.edition === "Shadowless" ? doc.edition : "";
  }

  function cardName(doc) {
    return (doc.character_names && doc.character_names[0]) || doc.display_title || doc.title || "View card";
  }

  function cardMetaLine(doc) {
    return [doc.set_name, doc.card_number, cardEditionLabel(doc), cardFinishLabel(doc)].filter(Boolean).join(" · ");
  }

  // Same fallback chain as hollowlog-search.js's getVariantId - the
  // Meilisearch document shape is shared, so a card's buyable variant
  // could be exposed under any of these keys depending on how it was
  // indexed.
  function getVariantId(doc) {
    if (!doc) return "";
    return (
      doc.variant_id ||
      doc.variantId ||
      doc.first_available_variant_id ||
      doc.firstAvailableVariantId ||
      doc.selected_or_first_available_variant_id ||
      doc.selectedOrFirstAvailableVariantId ||
      doc.default_variant_id ||
      doc.defaultVariantId ||
      (doc.variant && doc.variant.id) ||
      (Array.isArray(doc.variants) && doc.variants[0] && doc.variants[0].id) ||
      ""
    );
  }

  function renderAddToCartButton(doc) {
    const variantId = getVariantId(doc);
    if (!variantId) return "";

    return `
      <form method="post" action="/cart/add" class="hl-wl-add-form" data-wl-add-form>
        <input type="hidden" name="id" value="${escapeHtml(variantId)}">
        <input type="hidden" name="quantity" value="1">
        <button type="submit" class="hl-wl-add-to-cart">Add to cart</button>
      </form>
    `;
  }

  // What the shopper actually typed/uploaded for this row, shown as-is
  // so they recognise their own entry - not the backend's parsed guess.
  function rowQueryLabel(input) {
    if (!input || typeof input !== "object") return String(input || "");
    const parts = [input.name, input.set, input.number].filter(Boolean);
    return parts.length ? parts.join(" · ") : "(blank row)";
  }

  // The three shopper-facing buckets (spec: "2 matched / 3 need
  // checking / 1 not found") - the row itself still shows its own
  // precise exact/likely/similar/no_match badge.
  function bucketFor(status) {
    if (status === "exact") return "exact";
    if (status === "likely" || status === "similar") return "review";
    return "none";
  }

  // A candidate row has independent actions, so it can't be one big
  // <button> (nested interactive elements inside a button are invalid
  // HTML and misbehave) - the image opens a zoomed preview, and View/
  // Add to cart act on that specific listing. `pickable` controls
  // whether the title/meta/price also confirms the candidate as the
  // row's match: true for the similar/no_match tier's own grid (there
  // it's the only way to confirm anything, since no hero exists yet),
  // false for the exact/likely tier's side lists (clicking a "similar"
  // card there used to silently swap out an already-confirmed match
  // with no undo - clicking it now does nothing, only View/Add do).
  function renderCandidateChip(doc, index, resultIndex, listName, pickable) {
    const price = formatPrice(doc.price);
    const title = cardName(doc);
    const variantId = getVariantId(doc);

    const imageHtml = doc.image_url
      ? `
        <button type="button" class="hl-wl-candidate__image-btn" data-wl-zoom-image data-image-url="${escapeHtml(doc.image_url)}" data-title="${escapeHtml(title)}" aria-label="View a larger picture of ${escapeHtml(title)}">
          <img class="hl-wl-candidate__image" src="${escapeHtml(doc.image_url)}" alt="" loading="lazy" width="44" height="62">
        </button>
      `
      : `<span class="hl-wl-candidate__image hl-wl-candidate__image--placeholder"></span>`;

    const infoHtml = `
      <span class="hl-wl-candidate__info">
        <span class="hl-wl-candidate__title">${escapeHtml(title)}</span>
        <span class="hl-wl-candidate__meta">${escapeHtml(cardMetaLine(doc))}</span>
        ${price ? `<span class="hl-wl-candidate__price">${escapeHtml(price)}</span>` : ""}
      </span>
    `;
    const pickHtml = pickable
      ? `<button type="button" class="hl-wl-candidate__pick" data-wl-pick-candidate data-result-index="${resultIndex}" data-candidate-index="${index}" data-candidate-list="${listName}">${infoHtml}</button>`
      : `<div class="hl-wl-candidate__pick hl-wl-candidate__pick--static">${infoHtml}</div>`;

    return `
      <div class="hl-wl-candidate">
        ${imageHtml}
        ${pickHtml}
        <div class="hl-wl-candidate__actions">
          <a class="hl-wl-candidate__view" href="${escapeHtml(doc.url || "#")}" target="_blank" rel="noopener noreferrer" aria-label="View ${escapeHtml(title)} in a new tab">View</a>
          ${variantId ? `
            <form method="post" action="/cart/add" class="hl-wl-add-form hl-wl-candidate__add-form" data-wl-add-form>
              <input type="hidden" name="id" value="${escapeHtml(variantId)}">
              <input type="hidden" name="quantity" value="1">
              <button type="submit" class="hl-wl-add-to-cart hl-wl-candidate__add-to-cart">Add</button>
            </form>
          ` : ""}
        </div>
      </div>
    `;
  }

  // A single shared overlay for zoomed candidate images, created once
  // and reused - simpler than a per-chip modal, and keeps a fresh
  // rerender() from ever leaving a stray lightbox instance behind.
  function ensureLightbox() {
    let el = document.querySelector("[data-wl-lightbox]");
    if (el) return el;

    el = document.createElement("div");
    el.className = "hl-wl-lightbox";
    el.setAttribute("data-wl-lightbox", "");
    el.hidden = true;
    el.innerHTML = `
      <button type="button" class="hl-wl-lightbox__close" data-wl-lightbox-close aria-label="Close">✕</button>
      <img class="hl-wl-lightbox__image" data-wl-lightbox-image alt="">
    `;
    el.addEventListener("click", (event) => {
      if (event.target === el || event.target.closest("[data-wl-lightbox-close]")) {
        el.hidden = true;
      }
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && !el.hidden) el.hidden = true;
    });
    document.body.appendChild(el);
    return el;
  }

  function openLightbox(imageUrl, title) {
    if (!imageUrl) return;
    const el = ensureLightbox();
    const img = el.querySelector("[data-wl-lightbox-image]");
    img.src = imageUrl;
    img.alt = title || "";
    el.hidden = false;
  }

  function renderHero(doc, resultIndex, state) {
    const price = formatPrice(doc.price);
    const isConfirmed = state.confirmed;
    const title = cardName(doc);

    return `
      <div class="hl-wl-hero">
        ${doc.image_url
          ? `
            <button type="button" class="hl-wl-hero__image-btn" data-wl-zoom-image data-image-url="${escapeHtml(doc.image_url)}" data-title="${escapeHtml(title)}" aria-label="View a larger picture of ${escapeHtml(title)}">
              <img class="hl-wl-hero__image" src="${escapeHtml(doc.image_url)}" alt="" loading="lazy" width="180" height="252">
            </button>
          `
          : `<span class="hl-wl-hero__image hl-wl-hero__image--placeholder"></span>`}
        <div class="hl-wl-hero__info">
          <p class="hl-wl-hero__title">${escapeHtml(title)}</p>
          <p class="hl-wl-hero__meta">${escapeHtml(cardMetaLine(doc))}</p>
          ${price ? `<p class="hl-wl-hero__price">${escapeHtml(price)}</p>` : ""}
        </div>
        <div class="hl-wl-hero__actions">
          ${renderAddToCartButton(doc)}
          <a class="hl-wl-hero__link" href="${escapeHtml(doc.url || "#")}">View card</a>
          ${isConfirmed
            ? `<button type="button" class="hl-wl-hero__deselect" data-wl-deselect data-result-index="${resultIndex}" aria-pressed="true">✓ Selected</button>`
            : `<button type="button" class="hl-wl-hero__use" data-wl-use-match data-result-index="${resultIndex}">Use this match</button>`}
        </div>
      </div>
    `;
  }

  // Shown expanded by default - these are real, actionable options
  // (other listings of the same card, or other candidates worth a
  // look), not secondary detail worth hiding behind a click. `stacked`
  // renders a single-column list (the exact/likely tier's third
  // column, next to the hero) instead of the default multi-column grid
  // (the similar/no_match tier's browsable candidate grid). `listName`
  // ("other_listings" or "similar_matches") is stamped onto each
  // candidate chip so picking one can look it up in the right array on
  // the result, since an exact/likely row can show both lists at once.
  // `pickable` (default true) - see renderCandidateChip's docblock.
  function renderOthersToggle(items, resultIndex, label, listName, options) {
    if (!items || !items.length) return "";
    const stacked = Boolean(options && options.stacked);
    const pickable = !options || options.pickable !== false;

    return `
      <div class="hl-wl-others-group">
        <p class="hl-wl-others-label">${items.length} ${label}</p>
        <div class="hl-wl-others ${stacked ? "hl-wl-others--stacked" : ""}" data-wl-others>
          ${items.map((doc, i) => renderCandidateChip(doc, i, resultIndex, listName, pickable)).join("")}
        </div>
      </div>
    `;
  }

  function renderEditRow(input, resultIndex) {
    return `
      <div class="hl-wl-edit" data-wl-edit hidden>
        <input type="text" class="hl-wl-edit__input" value="${escapeHtml(rowQueryLabel(input))}" data-wl-edit-input>
        <button type="button" class="hl-wl-edit__submit" data-wl-edit-submit data-result-index="${resultIndex}" aria-label="Search again">\u{1F50D}</button>
      </div>
    `;
  }

  function renderRow(result, resultIndex, state) {
    if (result.skipped) {
      return `
        <li class="hl-wl-row hl-wl-row--skipped" data-wl-row data-result-index="${resultIndex}" data-bucket="none">
          <p class="hl-wl-row__query">${escapeHtml(rowQueryLabel(result.input))}</p>
          <p class="hl-wl-row__helper">Skipped - no name, number, or set given</p>
        </li>
      `;
    }

    const bucket = bucketFor(result.status);

    if (result.status === "exact" || result.status === "likely") {
      const badge = result.status === "exact"
        ? `<span class="hl-wl-badge hl-wl-badge--exact">✓ Exact match</span>`
        : `<span class="hl-wl-badge hl-wl-badge--likely">⚠ Check this match</span>`;

      // Candidates here are next to an already-confirmed match, not a
      // replacement for it - clicking one no longer swaps the row's
      // match out from under the shopper with no way back. Only View/
      // Add to cart (rendered regardless of pickable) act on them.
      const otherListings = result.other_listings || [];
      const similarMatches = result.similar_matches || [];
      const sideColumnHtml = [
        renderOthersToggle(otherListings, resultIndex, `other listing${otherListings.length === 1 ? "" : "s"} of this card`, "other_listings", { stacked: true, pickable: false }),
        renderOthersToggle(similarMatches, resultIndex, `similar ${escapeHtml(cardName(result.best_match))} card${similarMatches.length === 1 ? "" : "s"}`, "similar_matches", { stacked: true, pickable: false }),
      ].filter(Boolean).join("");

      return `
        <li class="hl-wl-row hl-wl-row--${result.status}" data-wl-row data-result-index="${resultIndex}" data-bucket="${bucket}">
          ${badge}
          <p class="hl-wl-row__query">${escapeHtml(rowQueryLabel(result.input))}</p>
          ${result.best_match.match_reason ? `<p class="hl-wl-row__reason">${escapeHtml(result.best_match.match_reason)}</p>` : ""}
          <div class="hl-wl-row-columns">
            ${renderHero(state.chosenCard || result.best_match, resultIndex, state)}
            ${sideColumnHtml ? `<div class="hl-wl-row-others-col">${sideColumnHtml}</div>` : ""}
          </div>
        </li>
      `;
    }

    // similar / no_match - no pre-selected hero, just an honest "we
    // couldn't confidently identify this" plus an editable/expandable
    // path forward. If the shopper has since picked a candidate from
    // the grid, show that as a confirmed hero instead.
    if (state.confirmed && state.chosenCard) {
      return `
        <li class="hl-wl-row hl-wl-row--confirmed" data-wl-row data-result-index="${resultIndex}" data-bucket="exact">
          <span class="hl-wl-badge hl-wl-badge--exact">✓ Selected</span>
          <p class="hl-wl-row__query">${escapeHtml(rowQueryLabel(result.input))}</p>
          ${renderHero(state.chosenCard, resultIndex, state)}
        </li>
      `;
    }

    const hasCandidates = result.similar_matches && result.similar_matches.length;
    const statusLine = hasCandidates
      ? "<strong>No exact match</strong> - we couldn't confidently identify this card in stock but found similar cards"
      : "<strong>Not found</strong> - we couldn't find this card in stock right now";

    return `
      <li class="hl-wl-row hl-wl-row--none" data-wl-row data-result-index="${resultIndex}" data-bucket="${bucket}">
        <p class="hl-wl-row__status">○ ${statusLine}</p>
        <button type="button" class="hl-wl-row__query hl-wl-row__query--editable" data-wl-toggle-edit data-result-index="${resultIndex}">${escapeHtml(rowQueryLabel(result.input))}</button>
        ${renderEditRow(result.input, resultIndex)}
        ${hasCandidates ? renderOthersToggle(result.similar_matches, resultIndex, `similar ${escapeHtml(rowQueryLabel(result.input).split(" · ")[0])} cards`, "similar_matches") : ""}
      </li>
    `;
  }

  function renderSummary(results, states) {
    const counts = { exact: 0, review: 0, none: 0 };
    results.forEach((result, i) => {
      if (result.skipped) return;
      const bucket = states[i].confirmed && bucketFor(result.status) !== "exact" ? "exact" : bucketFor(result.status);
      counts[bucket] += 1;
    });
    const total = results.filter((r) => !r.skipped).length;

    return `
      <div class="hl-wl-summary">
        <p class="hl-wl-summary__count">We checked ${total} card${total === 1 ? "" : "s"}</p>
        <p class="hl-wl-summary__breakdown">
          <span class="hl-wl-stat hl-wl-stat--exact">✓ ${counts.exact} matched</span>
          <span class="hl-wl-stat hl-wl-stat--review">⚠ ${counts.review} need${counts.review === 1 ? "s" : ""} checking</span>
          <span class="hl-wl-stat hl-wl-stat--none">${counts.none} not found</span>
        </p>
      </div>
    `;
  }

  function renderFilters(results, states, activeFilter) {
    const counts = { all: 0, exact: 0, review: 0, none: 0 };
    results.forEach((result, i) => {
      if (result.skipped) return;
      counts.all += 1;
      const bucket = states[i].confirmed && bucketFor(result.status) !== "exact" ? "exact" : bucketFor(result.status);
      counts[bucket] += 1;
    });

    const tabs = [
      { key: "all", label: `All ${counts.all}` },
      { key: "exact", label: `✓ Matched ${counts.exact}` },
      { key: "review", label: `⚠ Review ${counts.review}` },
      { key: "none", label: `Not found ${counts.none}` },
    ];

    return `
      <div class="hl-wl-filters" role="tablist" aria-label="Filter results">
        ${tabs.map((tab) => `
          <button type="button" class="hl-wl-filter ${tab.key === activeFilter ? "is-active" : ""}" data-wl-filter="${tab.key}" role="tab" aria-selected="${tab.key === activeFilter ? "true" : "false"}">
            ${escapeHtml(tab.label)}
          </button>
        `).join("")}
      </div>
    `;
  }

  function renderStickyBar(results, states) {
    const confirmedInCart = states.filter((s) => s.confirmed && getVariantId(s.chosenCard)).length;
    const reviewCount = results.filter((r, i) => !r.skipped && !states[i].confirmed && bucketFor(r.status) === "review").length;

    if (!results.some((r) => !r.skipped)) return "";

    return `
      <div class="hl-wl-sticky" data-wl-sticky>
        <p class="hl-wl-sticky__copy">
          <strong>${confirmedInCart} confirmed</strong>
          ${reviewCount ? `<span>· ${reviewCount} need${reviewCount === 1 ? "s" : ""} review</span>` : ""}
        </p>
        <button type="button" class="hl-wl-sticky__action" data-wl-add-confirmed-to-cart ${confirmedInCart === 0 ? "disabled" : ""}>
          Add ${confirmedInCart} to cart
        </button>
      </div>
    `;
  }

  function render(container, results, states, activeFilter) {
    const filtered = results
      .map((result, index) => ({ result, index }))
      .filter(({ result, index }) => {
        if (activeFilter === "all") return true;
        const bucket = states[index].confirmed && bucketFor(result.status) !== "exact" ? "exact" : bucketFor(result.status);
        return bucket === activeFilter;
      });

    container.innerHTML = `
      ${renderSummary(results, states)}
      ${renderFilters(results, states, activeFilter)}
      <ul class="hl-wl-rows">
        ${filtered.map(({ result, index }) => renderRow(result, index, states[index])).join("")}
      </ul>
      ${renderStickyBar(results, states)}
    `;
  }

  function setStatus(statusEl, message, isError) {
    statusEl.textContent = message || "";
    statusEl.classList.toggle("hl-wl-status--error", Boolean(isError));
  }

  // Confident matches surface first regardless of upload order - a
  // shopper's own list order carries no meaning here, and burying an
  // exact match at row 40 just because that's where it fell in their
  // CSV serves no one.
  const STATUS_RANK = { exact: 3, likely: 2, similar: 1, no_match: 0 };

  function sortResultsByConfidence(results) {
    return results
      .map((result, uploadIndex) => ({ result, uploadIndex }))
      .sort((a, b) => {
        const rankA = a.result.skipped ? -1 : (STATUS_RANK[a.result.status] ?? 0);
        const rankB = b.result.skipped ? -1 : (STATUS_RANK[b.result.status] ?? 0);
        if (rankA !== rankB) return rankB - rankA;
        return a.uploadIndex - b.uploadIndex;
      })
      .map(({ result }) => result);
  }

  function initialStateFor(result) {
    if (!result.skipped && result.status === "exact") {
      return { confirmed: true, chosenCard: result.best_match };
    }
    return { confirmed: false, chosenCard: null };
  }

  function init() {
    const page = document.querySelector(PAGE_MARKER);
    const apiBase = getApiBase();
    if (!page || !apiBase) return;

    const fileInput = page.querySelector("[data-hollowlog-want-list-file]");
    const textInput = page.querySelector("[data-hollowlog-want-list-text]");
    const detectedCountEl = page.querySelector("[data-wl-detected-count]");
    const submitButton = page.querySelector("[data-hollowlog-want-list-submit]");
    const tryExampleButton = page.querySelector("[data-wl-try-example]");
    const statusEl = page.querySelector("[data-hollowlog-want-list-status]");
    const resultsEl = page.querySelector("[data-hollowlog-want-list-results]");

    if (!fileInput || !submitButton || !statusEl || !resultsEl) return;

    let parsedRows = null;
    let currentResults = [];
    let rowStates = [];
    let activeFilter = "all";

    // "Find matches for 37 cards" once a count is known is a stronger,
    // more concrete call to action than a static label (UX review,
    // 2026-10-03) - falls back to the section's own configured label
    // (merchant-editable) whenever nothing's been typed/uploaded yet.
    const baseSubmitLabel = submitButton.dataset.wlBaseLabel || submitButton.textContent.trim();

    function updateSubmitLabel(count) {
      submitButton.textContent = count > 0
        ? `Find matches for ${count} card${count === 1 ? "" : "s"}`
        : baseSubmitLabel;
    }

    function rerender() {
      render(resultsEl, currentResults, rowStates, activeFilter);
    }

    fileInput.addEventListener("change", () => {
      resultsEl.innerHTML = "";
      parsedRows = null;
      submitButton.disabled = true;
      updateSubmitLabel(0);

      const file = fileInput.files && fileInput.files[0];
      if (!file) return;

      // The two entry modes are mutually exclusive - picking a file
      // clears anything pasted, so it's never ambiguous which one a
      // click on Check my list is actually going to submit.
      if (textInput) textInput.value = "";
      if (detectedCountEl) detectedCountEl.textContent = "";

      const reader = new FileReader();
      reader.onload = () => {
        const { rows, error } = parseCsv(String(reader.result || ""));
        if (error) {
          setStatus(statusEl, error, true);
          return;
        }
        parsedRows = rows;
        submitButton.disabled = false;
        updateSubmitLabel(rows.length);
        setStatus(statusEl, `${rows.length} card${rows.length === 1 ? "" : "s"} ready to check.`, false);
      };
      reader.onerror = () => setStatus(statusEl, "Couldn't read that file.", true);
      reader.readAsText(file);
    });

    if (textInput) {
      let debounceTimer = null;

      textInput.addEventListener("input", () => {
        clearTimeout(debounceTimer);

        debounceTimer = setTimeout(() => {
          resultsEl.innerHTML = "";
          parsedRows = null;
          submitButton.disabled = true;
          updateSubmitLabel(0);

          if (textInput.value.trim()) {
            fileInput.value = "";
          }

          const { rows, error } = parsePastedLines(textInput.value);

          if (detectedCountEl) {
            detectedCountEl.textContent = rows.length ? `${rows.length} card${rows.length === 1 ? "" : "s"} detected` : "";
          }

          if (error) {
            setStatus(statusEl, error, true);
            return;
          }
          if (!rows.length) {
            setStatus(statusEl, "", false);
            return;
          }

          parsedRows = rows;
          submitButton.disabled = false;
          updateSubmitLabel(rows.length);
          setStatus(statusEl, "", false);
        }, 200);
      });
    }

    // Demonstrates the parser's own tolerance for messy input (deliberately
    // mixed case, reordered fields, a near-miss number, a bare name, and
    // an edition-only line) rather than just describing it (UX review,
    // 2026-10-03) - "show, don't tell" for the one part of this page that
    // isn't obvious from looking at it: how forgiving the matching is.
    const EXAMPLE_LIST = [
      "charizard base set 4",
      "pikachu 60 jungle",
      "munchlax rising rivals 69",
      "light jolteon",
      "lt surge's rattata 1st edition",
    ].join("\n");

    if (tryExampleButton && textInput) {
      tryExampleButton.addEventListener("click", () => {
        textInput.value = EXAMPLE_LIST;
        textInput.dispatchEvent(new Event("input", { bubbles: true }));
        textInput.focus();
      });
    }

    async function runCheck(rows) {
      const response = await fetch(`${apiBase}/api/bulk-match`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rows }),
      });

      if (response.status === 503) {
        throw new Error("Search is temporarily unavailable - please try again shortly.");
      }
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Something went wrong - please try again.");
      }

      const data = await response.json();
      if (data.enabled === false) {
        throw new Error("This feature is temporarily unavailable.");
      }

      return data.results || [];
    }

    submitButton.addEventListener("click", async () => {
      if (!parsedRows || !parsedRows.length) return;

      submitButton.disabled = true;
      setStatus(statusEl, `Checking ${parsedRows.length} card${parsedRows.length === 1 ? "" : "s"}…`, false);
      resultsEl.innerHTML = "";

      try {
        currentResults = sortResultsByConfidence(await runCheck(parsedRows));
        rowStates = currentResults.map(initialStateFor);
        activeFilter = "all";
        setStatus(statusEl, "", false);
        rerender();
      } catch (error) {
        setStatus(statusEl, error.message, true);
      } finally {
        submitButton.disabled = false;
      }
    });

    resultsEl.addEventListener("click", async (event) => {
      const zoomButton = event.target.closest("[data-wl-zoom-image]");
      if (zoomButton) {
        openLightbox(zoomButton.getAttribute("data-image-url"), zoomButton.getAttribute("data-title"));
        return;
      }

      const filterButton = event.target.closest("[data-wl-filter]");
      if (filterButton) {
        activeFilter = filterButton.getAttribute("data-wl-filter");
        rerender();
        return;
      }

      const useMatchButton = event.target.closest("[data-wl-use-match]");
      if (useMatchButton) {
        const index = Number(useMatchButton.getAttribute("data-result-index"));
        rowStates[index] = { confirmed: true, chosenCard: currentResults[index].best_match };
        rerender();
        return;
      }

      const deselectButton = event.target.closest("[data-wl-deselect]");
      if (deselectButton) {
        const index = Number(deselectButton.getAttribute("data-result-index"));
        rowStates[index] = { confirmed: false, chosenCard: null };
        rerender();
        return;
      }

      const pickButton = event.target.closest("[data-wl-pick-candidate]");
      if (pickButton) {
        const index = Number(pickButton.getAttribute("data-result-index"));
        const candidateIndex = Number(pickButton.getAttribute("data-candidate-index"));
        const listName = pickButton.getAttribute("data-candidate-list");
        const doc = (currentResults[index][listName] || [])[candidateIndex];
        if (doc) {
          rowStates[index] = { confirmed: true, chosenCard: doc };
          rerender();
        }
        return;
      }

      const editToggle = event.target.closest("[data-wl-toggle-edit]");
      if (editToggle) {
        const row = editToggle.closest("[data-wl-row]");
        const editPanel = row ? row.querySelector("[data-wl-edit]") : null;
        if (editPanel) {
          editPanel.hidden = !editPanel.hidden;
          if (!editPanel.hidden) editPanel.querySelector("input").focus();
        }
        return;
      }

      const editSubmit = event.target.closest("[data-wl-edit-submit]");
      if (editSubmit) {
        const index = Number(editSubmit.getAttribute("data-result-index"));
        const row = editSubmit.closest("[data-wl-row]");
        const input = row ? row.querySelector("[data-wl-edit-input]") : null;
        const newText = input ? input.value.trim() : "";
        if (!newText) return;

        editSubmit.disabled = true;
        try {
          const [newResult] = await runCheck([{ name: newText }]);
          currentResults[index] = newResult;
          rowStates[index] = initialStateFor(newResult);
          rerender();
        } catch (error) {
          setStatus(statusEl, error.message, true);
        } finally {
          editSubmit.disabled = false;
        }
        return;
      }

      const addConfirmedToCartButton = event.target.closest("[data-wl-add-confirmed-to-cart]");
      if (addConfirmedToCartButton) {
        const items = [];
        rowStates.forEach((state) => {
          const variantId = state.confirmed && getVariantId(state.chosenCard);
          if (variantId) items.push({ id: variantId, quantity: 1 });
        });
        if (!items.length) return;

        const originalText = addConfirmedToCartButton.textContent;
        addConfirmedToCartButton.disabled = true;
        addConfirmedToCartButton.textContent = "Adding…";

        try {
          const response = await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ items, sections: CART_SECTIONS_TO_REFRESH, sections_url: window.location.pathname }),
          });
          if (!response.ok) throw new Error("Add to cart failed");

          const data = await response.json();
          refreshCartUi(data.sections);
          setStatus(statusEl, `Added ${items.length} card${items.length === 1 ? "" : "s"} to your cart.`, false);
        } catch (error) {
          setStatus(statusEl, "Couldn't add to cart - please try again.", true);
        } finally {
          addConfirmedToCartButton.disabled = false;
          addConfirmedToCartButton.textContent = originalText;
        }
      }
    });

    resultsEl.addEventListener("submit", async (event) => {
      const form = event.target.closest("[data-wl-add-form]");
      if (!form) return;
      event.preventDefault();

      const button = form.querySelector(".hl-wl-add-to-cart");
      if (!button) return;

      const originalText = button.textContent;
      button.disabled = true;
      button.textContent = "Adding…";

      try {
        const formData = new FormData(form);
        formData.append("sections", CART_SECTIONS_TO_REFRESH);
        formData.append("sections_url", window.location.pathname);

        const response = await fetch("/cart/add.js", {
          method: "POST",
          body: formData,
          headers: { Accept: "application/json" },
        });
        if (!response.ok) throw new Error("Add to cart failed");

        const data = await response.json();
        refreshCartUi(data.sections);
        button.textContent = "Added";

        setTimeout(() => {
          button.disabled = false;
          button.textContent = originalText;
        }, 1400);
      } catch (error) {
        button.disabled = false;
        button.textContent = "Try again";
      }
    });

    resultsEl.addEventListener("keydown", (event) => {
      const editInput = event.target.closest("[data-wl-edit-input]");
      if (editInput && event.key === "Enter") {
        event.preventDefault();
        const row = editInput.closest("[data-wl-row]");
        const submitBtn = row ? row.querySelector("[data-wl-edit-submit]") : null;
        if (submitBtn) submitBtn.click();
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
